import { IEMRListForUserAndPetResponseData } from "@/api_framework/api_modals/EMRs";
import { useV2EMRListAPI } from "@/app/hooks/api_hooks/emr/useV2EMRListAPI";
import { useRouter } from "@/app/hooks/useRouter";
import { format, fromUnixTime } from "date-fns";
import { FileText, Loader2, X } from "lucide-react";
import { useEffect, useState } from "react";

interface AllPreviousEMRModalProps {
  open: boolean;
  onClose: () => void;
  customerId?: string;
  petId?: string;
  /** The EMR currently open in the editor — highlighted, not hidden. */
  currentEmrId?: string | null;
}

const statusBadgeClass = (status?: string): string => {
  const s = String(status ?? "").toUpperCase();
  if (s === "COMPLETED") return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (s === "DRAFT" || s === "CREATED") return "bg-amber-50 text-amber-700 border-amber-200";
  return "bg-slate-100 text-slate-600 border-slate-200";
};

const formatWhen = (unix?: number): string => {
  if (!unix) return "—";
  try {
    return format(fromUnixTime(unix), "dd MMM yyyy, h:mm a");
  } catch {
    return "—";
  }
};

const AllPreviousEMRModal = ({ open, onClose, customerId, petId, currentEmrId }: AllPreviousEMRModalProps) => {
  const { getRouteKey } = useRouter();
  const { execute: fetchEmrList } = useV2EMRListAPI();
  const [emrs, setEmrs] = useState<IEMRListForUserAndPetResponseData[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !customerId || !petId) return;
    let active = true;
    setLoading(true);
    fetchEmrList(customerId, petId)
      .then((list) => {
        if (!active) return;
        const rows = Array.isArray(list) ? [...list] : [];
        rows.sort((a, b) => (b?.updated_at ?? b?.created_at ?? 0) - (a?.updated_at ?? a?.created_at ?? 0));
        setEmrs(rows);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [open, customerId, petId, fetchEmrList]);

  if (!open) return null;

  const viewEMR = (emrId: string) => {
    if (!customerId || !petId || !emrId) return;
    // New tab so the editor (and the floating recorder) stays put.
    window.open(`${getRouteKey("EMR_PDF_VIEW", "url")}/${customerId}/${petId}/${emrId}`, "_blank");
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/50 px-4 backdrop-blur-[2px]"
      onClick={onClose}
    >
      <div
        className="flex max-h-[80vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-[#0B6EFF]" />
            <h3 className="text-base font-semibold text-slate-900">All Previous EMR</h3>
            {!loading && (
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-500">
                {emrs.length}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-[160px] flex-1 overflow-y-auto px-5 py-4">
          {loading ? (
            <div className="flex h-40 items-center justify-center text-slate-400">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : emrs.length === 0 ? (
            <div className="flex h-40 flex-col items-center justify-center gap-2 text-slate-400">
              <FileText className="h-8 w-8" />
              <p className="text-sm font-medium">No EMRs found for this pet.</p>
            </div>
          ) : (
            <ul className="flex flex-col gap-2">
              {emrs.map((emr) => {
                const isCurrent = !!currentEmrId && emr.emr_id === currentEmrId;
                return (
                  <li
                    key={emr.emr_id}
                    className={`flex items-center justify-between gap-3 rounded-xl border px-4 py-3 transition-colors ${
                      isCurrent ? "border-[#0B6EFF]/40 bg-[#0B6EFF]/5" : "border-slate-200 hover:bg-slate-50"
                    }`}
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-slate-800">{formatWhen(emr.created_at)}</span>
                        {isCurrent && (
                          <span className="rounded-full bg-[#0B6EFF] px-2 py-0.5 text-[10px] font-bold text-white">
                            Current
                          </span>
                        )}
                      </div>
                      <div className="mt-0.5 flex items-center gap-2">
                        <span
                          className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${statusBadgeClass(
                            emr.emr_status,
                          )}`}
                        >
                          {emr.emr_status ?? "—"}
                        </span>
                        {emr.created_by && (
                          <span className="truncate text-xs text-slate-400">by {emr.created_by}</span>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => viewEMR(emr.emr_id)}
                      className="shrink-0 rounded-lg bg-[#0B6EFF] px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-[#0a5ed8]"
                    >
                      View
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default AllPreviousEMRModal;
